import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setTimesheetByDay, setTimesheetCalendar } from "../slices/myTimesheetSlice";
import * as services from "../service/MyTimesheetService";
import { setEmployeeListForTeam } from "features/employee/slices/employeeSlice";
import dayjs from "dayjs";
import { Alert, Flex } from "antd";
import { useTranslation } from "react-i18next";
import moment from "moment";
import styles from "../styles/MyTimesheet.module.css";
import MyTimesheetDetailModal from "../components/MyTimesheetDetailModal";

const useMyTimesheetTeamCalendar = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { employeeListForTeam } = useSelector((state) => state.employee);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const [pagination, setPagination] = useState({ page: 0, size: 15, pageSize: 15 });
    const [timesheetPagination, setTimesheetPagination] = useState({ page: 0, size: 10, pageSize: 10 });
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([]);
    const [selectedCell, setSelectedCell] = useState(null);
    const { timesheetCalendar, timesheetByDay, isLoading } = useSelector((state) => state.myTimesheet);
    const [currentDate, setCurrentDate] = useState(dayjs());
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [selectedEmployeeName, setSelectedEmployeeName] = useState(null);
    const [startDate, setStartDate] = useState(dayjs().startOf('month').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(dayjs().endOf('month').format("YYYY-MM-DD"));
    const [isModalOpen, setIsModalOpen] = useState(false);

    const getTimesheetCalendarData = useCallback(async (startDateParam, endDateParam, empId) => {
        try {
            dispatch(setIsLoading(true))

            const response = await services.getTimesheetCalendar({
                employeeId: empId || selectedEmployeeId,
                companyId: currentCompany,
                startDate: startDateParam,
                endDate: endDateParam,
            })
            dispatch(setTimesheetCalendar(response?.data?.content))
        } catch (error) {
            Alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, selectedEmployeeId, dispatch])

    const getAllEmployee = useCallback(async (searchText = "") => {
        try {
            dispatch(setIsLoading(true));
            const response = await services.getSubOrdinatesPaging({
                companyId: currentCompany,
                actionId: currentEmployee,
                employeeId: currentEmployee,
                page: pagination.page,
                size: pagination.size,
                searchName: searchText,
            })
            const employees = response?.data?.content || [];
            dispatch(setEmployeeListForTeam(employees))
            setTotal(response.data?.totalItems)

            if (employees.length > 0) {
                const firstEmp = employees[0];
                setSelectedEmployeeId(firstEmp?.empId);

                const employeeName = i18n.language === "en"
                    ? `${firstEmp?.nameEn || ''} ${firstEmp?.surNameEn || ''}`
                    : `${firstEmp?.name || ''} ${firstEmp?.surName || ''}`;
                setSelectedEmployeeName(employeeName);

                const selectedDate = dayjs(currentDate);
                const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
                const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");
                getTimesheetCalendarData(newStartDate, newEndDate, firstEmp?.empId);
            }
        } catch (error) {
            alert({ type: "error", resultObject: error })
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, currentEmployee, pagination.page, pagination.size, dispatch, i18n.language, currentDate, getTimesheetCalendarData])

    const employeeMenu = useMemo(() => {
        return employeeListForTeam?.map(employee => {
            return {
                key: employee?.empId,
                tab: employee,
                id: employee?.id,
                name: employee?.name,
                nameEn: employee?.nameEn,
                imageData: employee?.imageData
            }
        }) || []
    }, [employeeListForTeam])

    useEffect(() => {
        if (currentCompany) {
            getAllEmployee()
        }
    }, [currentCompany, pagination])

    useEffect(() => {
        if (employeeListForTeam && employeeListForTeam?.length > 0 && !selectedEmployeeId) {
            const firstEmp = employeeListForTeam[0];
            setSelectedEmployeeId(firstEmp?.empId);

            const employeeName = i18n.language === "en"
                ? `${firstEmp?.nameEn || ''} ${firstEmp?.surNameEn || ''}`
                : `${firstEmp?.name || ''} ${firstEmp?.surName || ''}`;
            setSelectedEmployeeName(employeeName);
        }
    }, [employeeListForTeam, selectedEmployeeId, selectedEmployeeName, i18n.language]);

    useEffect(() => {
        if (selectedEmployeeId && employeeListForTeam?.length > 0) {
            const selectedEmployee = employeeListForTeam.find(emp => emp?.empId === selectedEmployeeId);
            if (selectedEmployee) {
                const employeeName = i18n.language === "en"
                    ? `${selectedEmployee?.nameEn || ''} ${selectedEmployee?.surNameEn || ''}`
                    : `${selectedEmployee?.name || ''} ${selectedEmployee?.surName || ''}`;
                setSelectedEmployeeName(employeeName);
            }
        }
    }, [i18n.language, selectedEmployeeId, employeeListForTeam]);

    const handleSelectEmployee = (empId) => {
        setSelectedEmployeeId(empId);

        const selectedEmployee = employeeListForTeam.find(emp => emp?.empId === empId);
        if (selectedEmployee) {
            const employeeName = i18n.language === "en"
                ? `${selectedEmployee?.nameEn || ''} ${selectedEmployee?.surNameEn || ''}`
                : `${selectedEmployee?.name || ''} ${selectedEmployee?.surName || ''}`;
            setSelectedEmployeeName(employeeName);

            if (currentCompany) {
                const selectedDate = dayjs(currentDate);
                const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
                const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");
                getTimesheetCalendarData(newStartDate, newEndDate, empId);
            }
        }
    };

    const getTimesheetListByDay = useCallback(async (params, selectedDate, empId) => {
        try {
            dispatch(setIsLoading(true))

            const response = await services.getTimesheetListByDay({
                ...params,
                employeeId: empId || selectedEmployeeId,
                companyId: currentCompany,
                date: selectedDate,
            })

            dispatch(setTimesheetByDay(response?.data?.page?.content))
            setTimesheetPagination({
                ...params?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.totalPages
            });
        } catch (error) {
            // Alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, selectedEmployeeId, dispatch])

    const generateColumns = useCallback((selectedDate, empId) => {
        const momentDate = dayjs(selectedDate);
        const startOfMonth = momentDate.clone().startOf('month');
        const endOfMonth = momentDate.clone().endOf('month');
        const daysInMonth = endOfMonth.date();

        const baseColumns = [
            {
                title: <Flex justify="center" style={{ color: "#42526D", fontWeight: "normal", fontSize: "20px" }}>
                    {t("my_timesheet.label.date")}
                </Flex>,
                dataIndex: 'project',
                key: 'project',
                fixed: 'left',
                width: 300,
                render: (text, record) => {
                    if (record.type === 'summaryDay') {
                        return (
                            <div style={{ color: '#42526D', }}>
                                {t("my_timesheet.label.total_working_hour")}
                            </div>
                        );
                    }

                    const indent = {
                        project: 0,
                        role: 12,
                        task: 24,
                        subtask: 40,
                    }[record.type] || 0;

                    return (
                        <div style={{
                            paddingLeft: indent,
                            fontWeight: record.type === 'project' ? 'bold' : 'normal',
                            color: "#42526D"
                        }}>
                            {record.name}
                        </div>
                    );
                }
            }
        ];

        const dayColumns = [];

        const getDayName = (dayIndex) => {
            const days = i18n.language === 'en'
                ? ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
                : ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
            return days[dayIndex];
        };

        for (let day = 1; day <= daysInMonth; day++) {
            const currentDay = startOfMonth.clone().date(day);
            const dayOfWeek = getDayName(currentDay.day())
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
            const isToday = currentDay.isSame(moment(), 'day');
            const dateKey = currentDay.format('YYYY-MM-DD');

            dayColumns.push({
                title: (
                    <div style={{
                        textAlign: 'center',
                        backgroundColor: isToday ? "#2CADE7" : "transparent",
                        borderRadius: "4px",
                        padding: "4px"
                    }}>
                        <div style={{
                            fontWeight: 'bold', color: isToday ? "white" : "black"
                        }}>{day}</div>
                        <div style={{
                            fontSize: '12px',
                            color: isToday ? "white" : isWeekend ? '#ff4d4f' : '#9D9CA3',
                        }}>{dayOfWeek}</div>
                    </div>
                ),
                dataIndex: ['timeLogs', dateKey],
                key: dateKey,
                width: 80,
                align: 'center',
                className: isWeekend ? styles.weekendColumn : '',
                render: (value, record) => {
                    const handleClick = () => {
                        const isLeafTask =
                            record.type === "subtask" ||
                            (record.type === "task" && (!record.children || record.children.length === 0));

                        const isSummaryDay = record.type === 'summaryDay';
                        const isProjectSummary = record.type === 'project';

                        const canClickInNormalMode = (isLeafTask || record.type === "subtask" || isSummaryDay || isProjectSummary);

                        if (!(canClickInNormalMode)) return;

                        setSelectedCell({
                            value: value?.render,
                            txnMyTimeSheetId: value?.value,
                            record: {
                                ...record,
                                rawData: timesheetCalendar,
                                projectId: record.projectId,
                                roleId: record.roleId,
                                taskId: record.taskId,
                                subtaskId: record.subtaskId,
                                type: record.type,
                                billable: record.billable,
                                reworkFlag: record.reworkFlag,
                            },
                            date: dateKey,
                        });
                        setIsModalOpen(true);
                        getTimesheetListByDay({ page: 0, size: 10 }, dayjs(dateKey).format("YYYY-MM-DD"), empId);
                    };

                    const isLeafTask =
                        record.type === "subtask" ||
                        (record.type === "task" && (!record.children || record.children.length === 0));
                    const isSummaryDay = record.type === 'summaryDay';
                    const isProjectSummary = record.type === 'project';
                    const canClickInNormalMode = (isLeafTask || record.type === "subtask" || isSummaryDay || isProjectSummary);

                    const isClickable = canClickInNormalMode;

                    const displayValue = value?.render || "";
                    // const shouldShowClickableEmptyCell = isUpdateMode && isLeafTask && !value?.render;

                    return (
                        <div
                            style={{
                                cursor: isClickable ? 'pointer' : 'not-allowed',
                                minHeight: '20px',
                                // ...(shouldShowClickableEmptyCell)
                            }}
                            onClick={handleClick}
                        >
                            {displayValue}
                        </div>
                    );
                }
            });
        }
        setColumns([...baseColumns, ...dayColumns]);

    }, [t, timesheetCalendar, getTimesheetListByDay, i18n.language]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Draft':
                return '#A2ABBE';
            case 'Approved':
                return '#00BA34';
            case 'Pending':
                return '#EBD305';
            default:
                return '#A2ABBE';
        }
    };

    const formatWorkingTime = (hours, minutes, approveStatusEn, isSummary = false, isSummaryDay = false) => {
        const h = hours != null ? hours : 0;
        const m = minutes != null ? minutes : 0;

        const totalMinutes = h * 60 + m;
        let color;

        if (isSummaryDay) {
            color = totalMinutes >= 480 ? '#000000' : '#A2ABBE';
        } else if (isSummary) {
            color = '#A2ABBE';
        } else {
            color = getStatusColor(approveStatusEn);
        }

        return (
            <span
                style={{
                    fontWeight: 'bold',
                    cursor: "pointer",
                    color,
                    ...(isSummary
                        ? {}
                        : {
                            textDecorationLine: 'underline',
                        }),
                }}
            >
                {`${h}:${String(m).padStart(2, '0')}`}
            </span>
        );
    };


    const createSummaryRow = (data) => {
        const summaryTimeLogs = {};
        const summaryByProject = {};

        data.forEach((project) => {
            let projectTotalMinutes = 0;

            project.roleTaskMapping.forEach((mapping) => {
                mapping.task.forEach((task) => {
                    task.timeSheetList?.forEach((entry) => {
                        const date = entry.recordingDate;
                        const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);
                        // day
                        summaryTimeLogs[date] = (summaryTimeLogs[date] || 0) + totalMinutes;
                        // proj
                        projectTotalMinutes += totalMinutes;
                    });

                    task.children?.forEach((subtask) => {
                        subtask.timeSheetList?.forEach((entry) => {
                            const date = entry.recordingDate;
                            const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);
                            summaryTimeLogs[date] = (summaryTimeLogs[date] || 0) + totalMinutes;
                            projectTotalMinutes += totalMinutes;
                        });
                    });
                });
            });
            summaryByProject[project.projectName || project.projectCode || ""] = projectTotalMinutes;
        });

        const formattedTimeLogs = {};
        for (const [date, totalMinutes] of Object.entries(summaryTimeLogs)) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            formattedTimeLogs[date] = { render: formatWorkingTime(hours, minutes, '', false, true) };
        }

        const formattedProjectLogs = {};
        for (const [projectName, totalMinutes] of Object.entries(summaryByProject)) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            formattedProjectLogs[projectName] = formatWorkingTime(hours, minutes, '', true, false);
        }

        return [
            {
                key: 'summary-day-row',
                name: 'Total by Day',
                type: 'summaryDay',
                timeLogs: formattedTimeLogs,
            },
        ];
    };

    const transformTimesheetData = (data) => {
        const sortedData = [...data].sort((a, b) => {
            const nameA = (a.projectName || "")?.toLowerCase();
            const nameB = (b.projectName || "")?.toLowerCase();
            return nameA?.localeCompare(nameB, 'th');
        });

        const result = sortedData?.map((project) => {
            const projectNode = {
                key: project.id,
                name: project.projectName,
                type: 'project',
                projectId: project.id,
                timeLogs: {},
                children: [],
            };

            const projectTotalLogs = {};

            const sortedRoleMappings = [...project.roleTaskMapping].sort((a, b) => {
                const nameA = (a.role?.roleName || "")?.toLowerCase();
                const nameB = (b.role?.roleName || "")?.toLowerCase();
                return nameA?.localeCompare(nameB, 'th');
            });

            sortedRoleMappings?.forEach((mapping, i) => {
                const roleNode = {
                    key: `${project.id}-role-${i}`,
                    name: mapping.role?.roleName || '-',
                    type: 'role',
                    projectId: project.id,
                    roleId: mapping.role?.id,
                    timeLogs: {},
                    children: [],
                };

                const sortedTasks = [...(mapping.task || [])]?.sort((a, b) => {
                    const nameA = (a.taskName || "")?.toLowerCase();
                    const nameB = (b.taskName || "")?.toLowerCase();
                    return nameA?.localeCompare(nameB, 'th');
                });

                sortedTasks?.forEach((task) => {
                    if (!task?.taskName && !task?.timeSheetList?.length && !task?.children?.length) {
                        return;
                    }

                    const taskTimeLogs = {};

                    task.timeSheetList?.forEach((entry) => {
                        const date = entry.recordingDate;
                        const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);

                        taskTimeLogs[date] = {
                            value: entry.myTimeSheetId,
                            render: formatWorkingTime(
                                entry?.totalWorkingHours,
                                entry?.totalWorkingMinutes,
                                entry?.approveStatusEn,
                            )
                        }
                        projectTotalLogs[date] = (projectTotalLogs[date] || 0) + totalMinutes;
                    });
                    const taskNode = {
                        key: `${project.id}-task-${task.id}`,
                        name: task.taskName,
                        type: 'task',
                        projectId: project.id,
                        roleId: mapping.role?.id,
                        taskId: task.id,
                        billable: task.billable,
                        reworkFlag: task.reworkFlag,
                        timeSheetList: task.timeSheetList,
                        timeLogs: taskTimeLogs,
                        children: [],
                    };

                    const sortedSubtasks = [...(task.children || [])]?.sort((a, b) => {
                        const nameA = (a.taskName || "")?.toLowerCase();
                        const nameB = (b.taskName || "")?.toLowerCase();
                        return nameA?.localeCompare(nameB, 'th');
                    });

                    sortedSubtasks?.forEach((subtask) => {
                        const subtaskTimeLogs = {};

                        subtask.timeSheetList?.forEach((entry) => {

                            const date = entry.recordingDate;
                            const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);

                            subtaskTimeLogs[date] = {
                                value: entry.myTimeSheetId,
                                render: formatWorkingTime(
                                    entry?.totalWorkingHours,
                                    entry?.totalWorkingMinutes,
                                    entry?.approveStatusEn,
                                )
                            }

                            projectTotalLogs[date] = (projectTotalLogs[date] || 0) + totalMinutes;
                        });

                        const subtaskNode = {
                            key: `${project.id}-subtask-${subtask.id}`,
                            name: subtask.taskName,
                            type: 'subtask',
                            projectId: project.id,
                            roleId: mapping.role?.id,
                            taskId: task.id,
                            subtaskId: subtask.id,
                            billable: subtask.billable,
                            reworkFlag: subtask.reworkFlag,
                            timeLogs: subtaskTimeLogs,
                        };
                        taskNode.children.push(subtaskNode);
                    });

                    roleNode.children.push(taskNode);
                });

                projectNode.children.push(roleNode);
            });

            for (const [date, totalMinutes] of Object.entries(projectTotalLogs)) {
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                projectNode.timeLogs[date] = {
                    render: formatWorkingTime(hours, minutes, '', true)
                }
            }

            return projectNode;
        });

        const summaryRows = createSummaryRow(data);
        return [...summaryRows, ...result]
    };

    useEffect(() => {
        if (currentCompany && selectedEmployeeId) {
            generateColumns(currentDate, selectedEmployeeId);
        }
    }, [currentCompany, currentDate, selectedEmployeeId, generateColumns]);

    const onDateChange = useCallback(
        (date) => {
            if (!date || !currentCompany || !currentEmployee) return;

            const selectedDate = dayjs(date);
            const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
            const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");

            setCurrentDate(selectedDate);
            setStartDate(newStartDate);
            setEndDate(newEndDate);

            getTimesheetCalendarData(newStartDate, newEndDate);
        },
        [getTimesheetCalendarData, currentCompany, currentEmployee]
    );

    useEffect(() => {
        if (currentCompany && selectedEmployeeId) {
            const selectedDate = dayjs(currentDate);
            const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
            const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");
            getTimesheetCalendarData(newStartDate, newEndDate);
        }
    }, [currentCompany, currentDate])

    const modalDetail = useCallback(() => {
        return (
            <MyTimesheetDetailModal
                open={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                selectedCell={selectedCell}
                timesheetByDay={timesheetByDay}
                isTeamTimesheet={true}
                employeeName={selectedEmployeeName}
                isLoading={isLoading}
            />
        )
    }, [isModalOpen, selectedCell, timesheetByDay, selectedEmployeeName, isLoading])

    return {
        columns,
        pagination,
        total,
        isLoading,
        employeeMenu,
        currentDate,
        timesheetCalendar,
        setPagination,
        setTotal,
        handleSelectEmployee,
        transformTimesheetData,
        onDateChange,
        modalDetail,
        getAllEmployee,
    }
}
export default useMyTimesheetTeamCalendar;